// Assignment Completion dashboard card (ADR-093)
// Modeled on FixedTermContractsCard (ADR-091 pattern): navy header with a
// hero number, per-Cluster matrix body, chip + link actions. Numbers come from the
// ADR-093 summary endpoint — v1 is org-wide only (admin / people partner);
// other callers receive { supported: false } and see no card, so no card
// number can disagree with a reachable report view. Every number deep-links
// to the report with filters pre-applied via acrState.
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Tooltip,
} from '@mui/material';
import { COLORS } from '../../constants/radar';
import { assignmentCompletionApi } from '../../services/api/assignmentCompletionApi';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useAbility } from '../../contexts/AbilityContext';
import { useAuth } from '../../contexts/AuthContext';
import { ACTIONS, SUBJECTS } from '../../constants/permissions';
import { EMPLOYEE_REPORTING_SCOPE } from '../../constants/employeeReportingScope';
import {
  getCompletionRateColor,
  COMPLETION_RATE_THRESHOLDS,
  formatNames,
} from '../../constants/assignmentCompletion';

// Same dark blue theme as Team Feedback Overview / Fixed Term Contracts
const CARD_COLOR = '#2B436B';

const AssignmentCompletionCard = () => {
  const navigate = useNavigate();
  const ability = useAbility();
  const { user } = useAuth();
  const employee = user?.employee;

  const canView = ability.can(ACTIONS.READ, SUBJECTS.ASSIGNMENT_COMPLETION_REPORT);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!canView) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(false);
        const response = await assignmentCompletionApi.getAssignmentCompletionSummary();
        setData(response);
      } catch (err) {
        console.error('Error fetching assignment completion summary:', err);
        // The card must never block the dashboard — collapse to the error state
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [canView]);

  if (!canView) {
    return null;
  }

  // Defensive: older payloads / future scopes the client can't render.
  if (!loading && !error && data?.supported === false) {
    return null;
  }

  // Deep links seed acrState (the report's entry-state contract), with
  // backTo/backLabel returning to the dashboard. A hierarchy-scoped card
  // carries the representative preset (self + Hierarchy) so the report
  // reproduces exactly the numbers shown here — without it the card would
  // say one thing and its own link another (ADR-091's lesson).
  const goToReport = (extra = {}) => {
    const acrState = { ...extra, backTo: '/', backLabel: 'Home' };
    if (data?.period) {
      acrState.year = data.period.year;
    }
    if (data?.scope?.type === 'hierarchy' && employee?.id) {
      acrState.representative = { id: employee.id, fullName: employee.fullName };
      acrState.representativeScope = EMPLOYEE_REPORTING_SCOPE.HIERARCHY;
    }
    navigate('/reports/assignment-completion', { state: { acrState } });
  };

  if (loading) {
    return (
      <Card
        elevation={0}
        sx={{
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          backgroundColor: COLORS.BACKGROUND_LIGHT,
          width: 400,
          height: 240,
          mt: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <LoadingSpinner message="Loading assignment completion..." />
      </Card>
    );
  }

  const completionRate = data?.completionRate ?? null;
  const expectedCount = data?.expectedCount || 0;
  // Only completions inside a policied unit are "of the expected" — the raw
  // completedCount also counts work in clusters with no policy, which
  // belongs in the Completed column but not in this fraction.
  const completedCount = data?.policiedCompletedCount ?? data?.completedCount ?? 0;
  const missingCount = data?.missingCount || 0;
  const unpoliciedCount = data?.suCounts?.withoutPolicy ?? 0;
  // ADR-108: consultants on an excluded engagement model — scoped like every
  // other card number (delivery units, this caller's span). The model NAMES
  // are stated inline (product decision 2026-09-17), not hidden in a tooltip;
  // beyond three the line truncates and the full list moves to the tooltip.
  const excludedCount = data?.excludedByEngagementModelCount ?? 0;
  const excludedModelNames = (data?.excludedEngagementModels || []).map((model) => model.name);
  // The summary endpoint spreads the totals at the top level (summarizeRows)
  const laggingCount = data?.suCounts?.belowThreshold ?? 0;
  const breakdownRows = data?.breakdown?.rows || [];
  // The period is the year; the current year is computed year-to-date server-side
  const periodLabel = data?.period ? String(data.period.year) : '';

  const allClear = !error && expectedCount === 0;
  const showMatrix = !error && !allClear && breakdownRows.length > 0;
  // Org-wide cards group by cluster; a manager's card groups by the
  // business units their people sit in. (The payload's unitType keeps its
  // 'deliveryCenter' spelling — ADR-122 §9 sweeps the wire format, not this
  // label.)
  const isServiceUnitBreakdown = data?.breakdown?.unitType === 'serviceUnit';
  const unitLabel = isServiceUnitBreakdown ? 'Business Unit' : 'Cluster';

  const isHierarchy = data?.scope?.type === 'hierarchy';
  // "Organization-wide" vs "Your hierarchy · N people" — a manager must never
  // mistake their span's numbers for the company's.
  const scopeSuffix = isHierarchy
    ? ` · Your hierarchy (${data?.scope?.employeeCount ?? 0} ${(data?.scope?.employeeCount ?? 0) === 1 ? 'person' : 'people'})`
    : data?.scope?.type === 'organization'
      ? ' · Organization-wide'
      : '';

  // Nothing expected has two very different causes, and conflating them sends
  // the reader to the wrong place: a configuration gap belongs to whoever owns
  // the policies, an empty period belongs to nobody.
  const heroText = error
    ? null
    : allClear
      ? (unpoliciedCount > 0
        ? 'No cluster has a policy for this period'
        : isHierarchy ? 'No expected assignments in your hierarchy' : 'No expected assignments this period')
      : <><strong>{completionRate === null ? '—' : `${completionRate}%`}</strong> Completed · {periodLabel}</>;

  const linkStyle = {
    color: CARD_COLOR,
    fontSize: '0.75rem',
    fontWeight: 500,
    textDecoration: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  };

  const renderActions = (primaryLabel, primaryExtra) => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mt: 2 }}>
      {primaryLabel && (
        <Chip
          label={primaryLabel}
          variant="outlined"
          size="small"
          onClick={() => goToReport(primaryExtra)}
          sx={{
            backgroundColor: 'transparent',
            color: CARD_COLOR,
            borderColor: CARD_COLOR,
            borderWidth: 1,
            fontWeight: 500,
            fontSize: '0.75rem',
            cursor: 'pointer',
            '&:hover': { backgroundColor: 'rgba(43, 67, 107, 0.04)' },
          }}
        />
      )}
      <Link
        to="#"
        onClick={(e) => { e.preventDefault(); goToReport(); }}
        style={linkStyle}
        onMouseOver={(e) => { e.target.style.textDecoration = 'underline'; }}
        onMouseOut={(e) => { e.target.style.textDecoration = 'none'; }}
      >
        View Report →
      </Link>
    </Box>
  );

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: COLORS.BACKGROUND_LIGHT,
        width: showMatrix ? 440 : 400,
        mt: 1,
        overflow: 'hidden',
      }}
    >
      <CardContent sx={{ p: 0 }}>
        <Box sx={{ p: 2, backgroundColor: CARD_COLOR, color: 'white' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Assignment Completion
          </Typography>
          {heroText && (
            <Typography variant="body2">
              {heroText}
              {scopeSuffix}
            </Typography>
          )}
        </Box>

        {error ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3, gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Couldn't load summary
            </Typography>
            {renderActions(null)}
          </Box>
        ) : allClear ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4 }}>
            <Typography variant="body1" color="text.secondary">
              No eligible individuals with expected assignments in {periodLabel}
            </Typography>
            {renderActions(null)}
          </Box>
        ) : showMatrix ? (
          <Box sx={{ p: 2 }}>
            {/* Per-Cluster matrix: lagging clusters first (server-sorted).
                Only units with eligible people are rendered — a quiet one is
                dropped server-side, and the "Showing N of M" caption below
                accounts for the difference. */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1.7fr repeat(3, 1fr)', alignItems: 'center', rowGap: 0.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                {unitLabel}
              </Typography>
              {['Expected', 'Completed', '%'].map((h) => (
                <Typography
                  key={h}
                  variant="caption"
                  sx={{ fontWeight: 700, color: 'text.secondary', textAlign: 'right', whiteSpace: 'nowrap' }}
                >
                  {h}
                </Typography>
              ))}

              {breakdownRows.map((row) => {
                // One rendering rule, keyed off the payload's unitType — the
                // client never branches on the caller's role.
                const rowScope = row.id
                  ? (isServiceUnitBreakdown ? { businessUnitId: row.id } : { clusterId: row.id })
                  : {};
                return (
                  <React.Fragment key={row.id || 'unassigned'}>
                    <Typography
                      variant="body2"
                      onClick={() => goToReport(rowScope)}
                      sx={{ fontWeight: 600, cursor: 'pointer', py: 0.25, '&:hover': { textDecoration: 'underline' } }}
                    >
                      {row.label}
                      {/* ADR-122: Organizational Structure Type inline after the
                          cluster name — muted monospace code, full name on
                          hover. Same one presentation as ClusterSelector /
                          BusinessUnitSelector under showStructureType. Only
                          cluster rows carry it; service-unit rows come back
                          without one and render bare. */}
                      {row.orgStructureType?.code && (
                        <Typography
                          component="span"
                          title={row.orgStructureType.name}
                          sx={{
                            fontSize: '0.75rem',
                            fontWeight: 400,
                            color: '#6B7280',
                            fontFamily: 'monospace',
                            marginLeft: '6px',
                          }}
                        >
                          {row.orgStructureType.code}
                        </Typography>
                      )}
                    </Typography>
                    <Typography
                      variant="body2"
                      onClick={() => goToReport(rowScope)}
                      sx={{ textAlign: 'right', color: 'text.primary', cursor: 'pointer', py: 0.25 }}
                    >
                      {row.expectedCount === null ? '—' : row.expectedCount}
                    </Typography>
                    <Typography
                      variant="body2"
                      onClick={() => goToReport(rowScope)}
                      sx={{ textAlign: 'right', color: 'text.primary', cursor: 'pointer', py: 0.25 }}
                    >
                      {row.completedCount}
                    </Typography>
                    <Typography
                      variant="body2"
                      onClick={() => goToReport(rowScope)}
                      sx={{
                        textAlign: 'right',
                        fontWeight: 700,
                        color: getCompletionRateColor(row.completionRate),
                        cursor: 'pointer',
                        py: 0.25,
                      }}
                    >
                      {row.completionRate === null ? '—' : `${row.completionRate}%`}
                    </Typography>
                  </React.Fragment>
                );
              })}
            </Box>

            {data?.breakdown?.totalUnits > breakdownRows.length && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Showing {breakdownRows.length} of {data.breakdown.totalUnits}{' '}
                {isServiceUnitBreakdown ? 'business units' : 'clusters'}
              </Typography>
            )}

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {completedCount} of {expectedCount} expected assignments completed
              {missingCount > 0 ? ` · ${missingCount} never created` : ''}
              {/* Explains an Expected total that looks too small: those units
                  are unconfigured, not idle. */}
              {unpoliciedCount > 0
                ? ` · ${unpoliciedCount} ${unpoliciedCount === 1 ? 'unit' : 'units'} not measured (no policy)`
                : ''}
              {' · Delivery units'}
            </Typography>
            {excludedCount > 0 && (
              <Tooltip title={excludedModelNames.length > 3 ? excludedModelNames.join(', ') : ''}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {excludedCount} {excludedCount === 1 ? 'individual' : 'individuals'} not measured —
                  {' '}excluded engagement {excludedModelNames.length === 1 ? 'model' : 'models'}:
                  {' '}{formatNames(excludedModelNames)}
                </Typography>
              </Tooltip>
            )}

            {renderActions(
              laggingCount > 0 ? 'Review Lagging' : null,
              { belowRate: COMPLETION_RATE_THRESHOLDS.LAGGING, orderDirection: 'asc' },
            )}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3, gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {completedCount} of {expectedCount} expected assignments completed · Delivery units
            </Typography>
            {renderActions(
              laggingCount > 0 ? 'Review Lagging' : null,
              { belowRate: COMPLETION_RATE_THRESHOLDS.LAGGING, orderDirection: 'asc' },
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default AssignmentCompletionCard;
